import React, { useEffect, useState, useRef } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { collection, doc, getDoc, getDocs, getFirestore, onSnapshot, deleteDoc } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  ArrowLeft, 
  Monitor, 
  Plus, 
  CheckCircle, 
  XCircle,
  User,
  X,
  Eye,
  EyeOff,
  Settings,
  RefreshCw,
  Wifi,
  WifiOff,
  Trash2,
  AlertTriangle
} from 'lucide-react';

function InstantLogViewer({ initialDevice, onBack, allDevices }) {
  const [devices, setDevices] = useState([initialDevice]);
  const [deviceData, setDeviceData] = useState({});
  const [allFields, setAllFields] = useState([]);
  const [hiddenFields, setHiddenFields] = useState(new Set());
  const [listeners, setListeners] = useState({});
  const [deviceInfo, setDeviceInfo] = useState({});
  const [availableDevices, setAvailableDevices] = useState([]);
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showClearLogDialog, setShowClearLogDialog] = useState(false);
  const [deviceToClearLog, setDeviceToClearLog] = useState(null);
  const [isClearingLog, setIsClearingLog] = useState(false);
  const [flashingCells, setFlashingCells] = useState(new Set());
  const deviceDataRef = useRef({});

  // Initialize available devices for dropdown
  useEffect(() => {
    const initializeAvailableDevices = async () => {
      const db = getFirestore(app);
      const devicesWithNames = await Promise.all(
        allDevices.map(async (device) => {
          try {
            const familySnap = await getDoc(doc(db, 'familyInfo', device.id));
            const familyName = familySnap.exists() ? familySnap.data().name : device.id;
            return { ...device, familyName };
          } catch (e) {
            console.error(`Could not fetch family info for ${device.id}`, e);
            return { ...device, familyName: device.id };
          }
        })
      );
      setAvailableDevices(devicesWithNames);
    };

    if (allDevices && allDevices.length > 0) {
      initializeAvailableDevices();
    }
  }, [allDevices]);

  // Keep deviceDataRef in sync with deviceData state
  useEffect(() => {
    deviceDataRef.current = deviceData;
  }, [deviceData]);

  // Update current time every second for live countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Load device info (family name and online status)
  useEffect(() => {
    const loadDeviceInfo = async () => {
      const db = getFirestore(app);
      const info = {};
      
      for (const device of devices) {
        try {
          const familySnap = await getDoc(doc(db, 'familyInfo', device.id));
          const familyName = familySnap.exists() ? familySnap.data().name : device.id;
          
          // Check online status
          const pingPeriodSeconds = 30;
          const pingBufferSeconds = 5;
          const onlinePingDate = device.onlinePing?.toDate();
          const isOnline = onlinePingDate && (Date.now() - onlinePingDate.getTime() < (pingPeriodSeconds + pingBufferSeconds) * 1000);
          
          info[device.id] = {
            familyName,
            isOnline
          };
        } catch (e) {
          console.error(`Could not fetch info for ${device.id}`, e);
          info[device.id] = {
            familyName: device.id,
            isOnline: false
          };
        }
      }
      
      setDeviceInfo(info);
    };

    loadDeviceInfo();
  }, [devices]);

  // Set up Firestore listeners for all devices
  useEffect(() => {
    const db = getFirestore(app);
    const newListeners = {};
    
    devices.forEach(device => {
      // Listen to different log document types
      ['system', 'audio', 'control', 'call', 'network', 'camera', 'zoom'].forEach(logType => {
        const logRef = doc(db, 'deviceManagement', device.id, 'log', logType);
        
        const unsubscribe = onSnapshot(logRef, 
          (docSnap) => {
            // Success callback
            if (docSnap.exists()) {
              const newData = docSnap.data();
              const oldData = deviceDataRef.current[device.id]?.[logType] || {};
              
              // Check for changed fields and trigger flash
              Object.keys(newData).forEach(fieldName => {
                const fieldPath = `${logType}.${fieldName}`;
                const oldValue = oldData[fieldName];
                const newValue = newData[fieldName];
                
                // Only flash if value actually changed (not just initial load)
                if (oldValue !== undefined && oldValue !== newValue) {
                  const cellKey = `${device.id}_${fieldPath}`;
                  console.log(`Flash triggered for ${cellKey}: ${oldValue} -> ${newValue}`);
                  setFlashingCells(prev => new Set(prev).add(cellKey));
                  
                  // Remove flash after 500ms
                  setTimeout(() => {
                    setFlashingCells(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(cellKey);
                      return newSet;
                    });
                  }, 500);
                }
              });
              
              setDeviceData(prev => ({
                ...prev,
                [device.id]: {
                  ...prev[device.id],
                  [logType]: newData
                }
              }));
              const now = Date.now();
              setLastUpdateTime(now);
              setCurrentTime(now);
              setConnectionStatus(prev => ({
                ...prev,
                [`${device.id}_${logType}`]: 'connected'
              }));
              console.log(`Updated ${logType} data for device ${device.id}`);
            } else {
              // Document doesn't exist - clear the data for this log type
              setDeviceData(prev => ({
                ...prev,
                [device.id]: {
                  ...prev[device.id],
                  [logType]: {}
                }
              }));
            }
          }, 
          (error) => {
            // Error callback
            console.error(`Error listening to ${logType} log for device ${device.id}:`, error);
            setConnectionStatus(prev => ({
              ...prev,
              [`${device.id}_${logType}`]: 'error'
            }));
            
            // Try to reconnect after a delay
            setTimeout(() => {
              console.log(`Attempting to reconnect listener for ${logType} on device ${device.id}`);
              
              // Recreate the listener
              const retryUnsubscribe = onSnapshot(logRef, 
                (docSnap) => {
                  if (docSnap.exists()) {
                    const data = docSnap.data();
                    setDeviceData(prev => ({
                      ...prev,
                      [device.id]: {
                        ...prev[device.id],
                        [logType]: data
                      }
                    }));
                    const now = Date.now();
                    setLastUpdateTime(now);
                    setCurrentTime(now);
                    console.log(`Reconnected and updated ${logType} data for device ${device.id}`);
                  }
                }, 
                (retryError) => {
                  console.error(`Failed to reconnect listener for ${logType} on device ${device.id}:`, retryError);
                }
              );
              
              // Update the listeners map with the new listener
              setListeners(prev => ({
                ...prev,
                [`${device.id}_${logType}`]: retryUnsubscribe
              }));
            }, 5000); // Wait 5 seconds before retry
          }
        );
        
        newListeners[`${device.id}_${logType}`] = unsubscribe;
      });
    });

    // Cleanup old listeners
    Object.values(listeners).forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (error) {
        console.warn('Error cleaning up listener:', error);
      }
    });
    setListeners(newListeners);

    return () => {
      Object.values(newListeners).forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          console.warn('Error cleaning up listener on unmount:', error);
        }
      });
    };
  }, [devices]);

  // Calculate superset of all fields across all devices
  useEffect(() => {
    const fieldsSet = new Set();
    
    Object.values(deviceData).forEach(deviceLogs => {
      Object.entries(deviceLogs).forEach(([logType, logData]) => {
        Object.keys(logData).forEach(field => {
          fieldsSet.add(`${logType}.${field}`);
        });
      });
    });
    
    const sortedFields = Array.from(fieldsSet).sort();
    setAllFields(sortedFields);
  }, [deviceData]);

  const addDevice = (deviceId) => {
    const deviceToAdd = availableDevices.find(d => d.id === deviceId);
    if (deviceToAdd && !devices.find(d => d.id === deviceId)) {
      setDevices(prev => [...prev, deviceToAdd]);
    }
  };

  const removeDevice = (deviceId) => {
    setDevices(prev => prev.filter(d => d.id !== deviceId));
    // Clean up device data
    setDeviceData(prev => {
      const newData = { ...prev };
      delete newData[deviceId];
      return newData;
    });
  };

  const getFieldValue = (deviceId, fieldPath) => {
    const [logType, ...fieldParts] = fieldPath.split('.');
    const fieldName = fieldParts.join('.');
    const deviceLogs = deviceData[deviceId];
    
    if (!deviceLogs || !deviceLogs[logType]) {
      return '-';
    }
    
    const value = deviceLogs[logType][fieldName];
    if (value === undefined || value === null) {
      return '-';
    }
    
    // Format different types of values
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    
    if (typeof value === 'number') {
      return value.toString();
    }
    
    if (typeof value === 'object' && value.toDate) {
      // Firestore timestamp
      return value.toDate().toLocaleString();
    }
    
    return String(value);
  };

  const getAvailableDevicesForDropdown = () => {
    return availableDevices.filter(device => 
      !devices.find(d => d.id === device.id)
    );
  };

  const toggleFieldVisibility = (field) => {
    setHiddenFields(prev => {
      const newHidden = new Set(prev);
      if (newHidden.has(field)) {
        newHidden.delete(field);
      } else {
        newHidden.add(field);
      }
      return newHidden;
    });
  };

  const showAllFields = () => {
    setHiddenFields(new Set());
  };

  const hideAllFields = () => {
    setHiddenFields(new Set(allFields));
  };

  const refreshAllConnections = () => {
    console.log('Manually refreshing all connections...');
    // Force re-setup of all listeners by updating devices state
    setDevices(prev => [...prev]);
    setConnectionStatus({});
    const now = Date.now();
    setLastUpdateTime(now);
    setCurrentTime(now);
  };

  const getConnectionStatus = (deviceId) => {
    const deviceConnections = Object.keys(connectionStatus)
      .filter(key => key.startsWith(`${deviceId}_`))
      .map(key => connectionStatus[key]);
    
    if (deviceConnections.length === 0) return 'connecting';
    if (deviceConnections.some(status => status === 'error')) return 'error';
    if (deviceConnections.every(status => status === 'connected')) return 'connected';
    return 'connecting';
  };

  const getTimeSinceLastUpdate = () => {
    const secondsAgo = Math.floor((currentTime - lastUpdateTime) / 1000);
    
    // Ensure we never show negative time
    const actualSecondsAgo = Math.max(0, secondsAgo);
    
    if (actualSecondsAgo < 60) {
      return `${actualSecondsAgo}s`;
    } else if (actualSecondsAgo < 3600) {
      const minutesAgo = Math.floor(actualSecondsAgo / 60);
      const remainingSeconds = actualSecondsAgo % 60;
      return remainingSeconds > 0 ? `${minutesAgo}m ${remainingSeconds}s` : `${minutesAgo}m`;
    } else {
      const hoursAgo = Math.floor(actualSecondsAgo / 3600);
      const remainingMinutes = Math.floor((actualSecondsAgo % 3600) / 60);
      return remainingMinutes > 0 ? `${hoursAgo}h ${remainingMinutes}m` : `${hoursAgo}h`;
    }
  };

  const handleClearLogClick = (device) => {
    setDeviceToClearLog(device);
    setShowClearLogDialog(true);
  };

  const handleClearLogConfirm = async () => {
    if (!deviceToClearLog) return;
    
    setIsClearingLog(true);
    try {
      const db = getFirestore(app);
      
      // Get all documents in the log subcollection for this device
      const logCollectionRef = collection(db, 'deviceManagement', deviceToClearLog.id, 'log');
      const logSnapshot = await getDocs(logCollectionRef);
      
      // Delete all log documents
      const deletePromises = [];
      logSnapshot.docs.forEach(logDoc => {
        deletePromises.push(deleteDoc(logDoc.ref));
      });
      
      // Wait for all deletions to complete
      await Promise.all(deletePromises);
      
      // Clear local device data for this device
      setDeviceData(prev => ({
        ...prev,
        [deviceToClearLog.id]: {}
      }));
      
      console.log(`Cleared ${deletePromises.length} log documents for device: ${deviceInfo[deviceToClearLog.id]?.familyName || deviceToClearLog.id} (${deviceToClearLog.id})`);
    } catch (error) {
      console.error('Error clearing device log:', error);
      alert('Failed to clear device log. Please try again.');
    } finally {
      setIsClearingLog(false);
      setShowClearLogDialog(false);
      setDeviceToClearLog(null);
    }
  };

  const handleClearLogCancel = () => {
    setShowClearLogDialog(false);
    setDeviceToClearLog(null);
  };

  const visibleFields = allFields.filter(field => !hiddenFields.has(field));
  const hiddenFieldsList = allFields.filter(field => hiddenFields.has(field));

  return (
    <div className="mx-auto p-6 min-h-screen flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Device Management
          </Button>
          <Monitor className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Instant Log Viewer</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center text-sm text-muted-foreground">
            <span>Last update:&nbsp;</span>
            <span className="min-w-8 text-right font-mono">{getTimeSinceLastUpdate()}</span>
            <span>&nbsp;ago</span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={refreshAllConnections}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowFieldManager(!showFieldManager)}
            className="flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Manage Fields
          </Button>
        </div>
      </div>

      {/* Field Management Panel */}
      {showFieldManager && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Field Management
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={showAllFields}>
                  Show All ({allFields.length})
                </Button>
                <Button size="sm" variant="outline" onClick={hideAllFields}>
                  Hide All
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Visible Fields */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Visible Fields ({visibleFields.length})
                </h4>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {visibleFields.map(field => (
                    <div key={field} className="flex items-center justify-between p-2 rounded border">
                      <span className="text-sm font-mono">{field}</span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => toggleFieldVisibility(field)}
                        className="h-6 px-2"
                      >
                        <EyeOff className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hidden Fields */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <EyeOff className="w-4 h-4" />
                  Hidden Fields ({hiddenFieldsList.length})
                </h4>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {hiddenFieldsList.map(field => (
                    <div key={field} className="flex items-center justify-between p-2 rounded border bg-muted/50">
                      <span className="text-sm font-mono text-muted-foreground">{field}</span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => toggleFieldVisibility(field)}
                        className="h-6 px-2"
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Device Management */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Devices:</span>
          {devices.map(device => (
            <div key={device.id} className="flex items-center gap-1 bg-muted rounded-lg px-3 py-1">
              {deviceInfo[device.id]?.isOnline ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm">{deviceInfo[device.id]?.familyName || device.id}</span>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => removeDevice(device.id)}
                className="h-5 w-5 p-0 ml-1"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
        
        {getAvailableDevicesForDropdown().length > 0 && (
          <div className="flex items-center gap-2">
            <Select onValueChange={addDevice}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Add device..." />
              </SelectTrigger>
              <SelectContent>
                {getAvailableDevicesForDropdown().map(device => (
                  <SelectItem key={device.id} value={device.id}>
                    <div className="flex items-center gap-2">
                      {device.onlinePing && (() => {
                        const pingPeriodSeconds = 30;
                        const pingBufferSeconds = 5;
                        const onlinePingDate = device.onlinePing?.toDate();
                        const isOnline = onlinePingDate && (Date.now() - onlinePingDate.getTime() < (pingPeriodSeconds + pingBufferSeconds) * 1000);
                        return isOnline ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        );
                      })()}
                      <span>{device.familyName}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Data Table */}
      <Card className="flex-1 flex flex-col mb-24">
        <CardContent className="p-0 flex-1 flex flex-col">
          <div className="overflow-auto flex-1">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0 z-10">
                <tr>
                  <th className="text-left py-1 px-2 font-medium border-r min-w-[200px] bg-muted/50">Field</th>
                  {devices.map(device => (
                    <th key={device.id} className="text-center py-1 px-2 font-medium border-r min-w-[150px] relative group bg-muted/50">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-2">
                          {deviceInfo[device.id]?.isOnline ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="truncate">
                            {deviceInfo[device.id]?.familyName || device.id}
                          </span>
                          {(() => {
                            const status = getConnectionStatus(device.id);
                            if (status === 'connected') {
                              return <Wifi className="w-3 h-3 text-green-500" />;
                            } else if (status === 'error') {
                              return <WifiOff className="w-3 h-3 text-red-500" />;
                            } else {
                              return <RefreshCw className="w-3 h-3 text-yellow-500 animate-spin" />;
                            }
                          })()}
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {device.id}
                        </span>
                        <div className="flex gap-1 mt-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearLogClick(device);
                            }}
                            className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Clear Log
                          </Button>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeDevice(device.id);
                        }}
                        className="absolute top-1 right-1 h-6 w-6 p-1 opacity-60 group-hover:opacity-100 transition-opacity hover:bg-red-100 hover:text-red-600 border border-gray-300 bg-white/80"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleFields.map((field, index) => (
                  <tr key={field} className={`border-b hover:bg-muted/30 ${index % 2 === 0 ? 'bg-muted/10' : ''}`}>
                    <td className="py-0.5 px-2 font-mono text-sm border-r bg-muted/20 sticky left-0 z-10">
                      <div className="flex items-center justify-between group">
                        <span>{field}</span>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFieldVisibility(field);
                          }}
                          className="h-6 w-6 p-1 opacity-60 group-hover:opacity-100 transition-opacity hover:bg-gray-100 border border-gray-300 bg-white/80"
                        >
                          <EyeOff className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                    {devices.map(device => {
                      const cellKey = `${device.id}_${field}`;
                      const isFlashing = flashingCells.has(cellKey);
                      return (
                        <td 
                          key={device.id} 
                          className={`py-0.5 px-2 text-center font-mono text-sm border-r transition-colors duration-200 ${
                            isFlashing ? 'bg-red-100 text-red-700' : ''
                          }`}
                        >
                          {getFieldValue(device.id, field)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            
            {visibleFields.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {allFields.length === 0 ? 'No log data available' : 'All fields are hidden'}
                </p>
                {allFields.length > 0 && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={showAllFields}
                    className="mt-2"
                  >
                    Show All Fields
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Clear Log Confirmation Dialog */}
      {showClearLogDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Clear Device Log</h3>
            </div>
            <p className="text-gray-600 mb-6">
              This will permanently delete all current log data for device: <strong>{deviceInfo[deviceToClearLog?.id]?.familyName || deviceToClearLog?.id}</strong>
              <br /><br />
              This action cannot be undone. Are you sure you want to continue?
            </p>
            <div className="flex gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={handleClearLogCancel}
                disabled={isClearingLog}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleClearLogConfirm}
                disabled={isClearingLog}
                className="flex items-center gap-2"
              >
                {isClearingLog ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Clear Log
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstantLogViewer;